import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { TabList, Tab } from '@fluentui/react-components';
import LastSyncDetailsTable from './PagesComponents/LastSyncDetailsTable';
import SyncHistoryDetails from './PagesComponents/SyncHistoryDetails';
import CurrentCustomerInformationDetails from './PagesComponents/CurrentCustomerInformationDetails';
import { CONNECTOR_GROUPS } from '../utils/connectorGroups';
import { aggregateSyncData, getMatchedSyncStatus, getPercentageColor } from '../utils/syncStatusHelpers';

const tabListStyles = `
  .sync-details-container {
    margin: 20px auto;
    max-width: 1600px;
    padding-left: 40px;
    padding-right: 40px;
    box-sizing: border-box;
    width: 100%;
  }
  .custom-tab-list {
    margin-bottom: 24px;
    border-bottom: 2px solid #e1dfdd;
    overflow-x: auto;
    scrollbar-width: thin;
  }

  .custom-tab {
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 500;
    min-height: 44px;
    margin-right: 8px;
    border-radius: 4px 4px 0 0;
    border: 1px solid transparent;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .custom-tab:hover {
    background-color: #f3f2f1;
  }
  .custom-tab[aria-selected="true"] {
    background-color: transparent;
    font-weight: 600;
    border-top: 3px solid #0078d4;
    border-left: 3px solid #0078d4;
    border-right: 3px solid #0078d4;
    border-bottom: 3px solid #0078d4;
    border-radius: 8px 8px 0 0;
    position: relative;
    z-index: 1;
  }

  @media (max-width: 1400px) {
    .sync-details-container {
      padding-left: 20px;
      padding-right: 20px;
      margin: 12px auto;
    }
    .custom-tab {
      padding: 8px 10px;
      font-size: 12px;
      min-height: 40px;
      margin-right: 6px;
    }
    .custom-tab > span {
      gap: 6px;
    }
    .custom-tab > span > span {
      font-size: 12px;
    }
    .custom-tab-list {
      margin-bottom: 20px;
    }
  }

  @media (max-width: 1200px) {
    .sync-details-container {
      padding-left: 15px;
      padding-right: 15px;
      margin: 11px auto;
    }
    .custom-tab {
      padding: 8px 4px;
      font-size: 11px;
      min-height: 40px;
      margin-right: 6px;
    }
    .custom-tab > span {
      gap: 6px;
    }
    .custom-tab > span > span {
      font-size: 11px;
    }
    .custom-tab-list {
      margin-bottom: 20px;
    }
  }
`;

function SyncDetails() {
  const { tenantInt, customerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lastSyncData, setLastSyncData] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const validTabKeys = useMemo(() => CONNECTOR_GROUPS.map(g => g.key), []);
  const [selectedConnector, setSelectedConnector] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl && validTabKeys.includes(tabFromUrl) ? tabFromUrl : 'all';
  });

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && validTabKeys.includes(tabFromUrl)) {
      setSelectedConnector(tabFromUrl);
    } else {
      setSelectedConnector('all');
    }
  }, [searchParams, validTabKeys]);

  useEffect(() => {
    if (!tenantInt) {
      setError('Tenant connectors are not configured for this customer');
      setLoading(false);
      return;
    }

    const tenantIntValue = parseInt(tenantInt, 10);
    if (isNaN(tenantIntValue)) {
      setError('Tenant connectors are not configured for this customer');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/syncs?TenantInt=${tenantIntValue}`;

    fetch(url)
      .then((res) => {
        if (res.status === 204) {
          setLastSyncData([]);
          setLoading(false);
          return null;
        }
        if (!res.ok) {
          throw new Error('Failed to fetch sync details');
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          setLastSyncData(data);
        } else {
          setLastSyncData([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch sync details:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [tenantInt]);

  useEffect(() => {
    if (!customerId) {
      return;
    }

    const customerIdValue = parseInt(customerId, 10);
    const tenantIntValue = tenantInt ? parseInt(tenantInt, 10) : null;
    
    if (customerIdValue === 9999999 || tenantIntValue === 9999999) {
      setCustomer({
        CustomerID: 9999999,
        TenantInt: 9999999,
        CompanyName: "All Tenants Syncs - these syncs impact all customers",
        TenantName: "Global - these syncs impact all customers"
      });
    }
    else
    {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}`)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error('Customer not found');
          }
          throw new Error('Failed to fetch customer information');
        }
        return res.json();
      })
      .then((data) => {
        setCustomer(data);
      })
      .catch((err) => {
        console.error('Failed to fetch customer information:', err);
      });
    }
  }, [customerId, tenantInt]);

  const filteredLastSyncData = useMemo(() => {
    if (!selectedConnector || selectedConnector === 'all') return lastSyncData;

    const group = CONNECTOR_GROUPS.find(g => g.key === selectedConnector);
    if (!group || group.startedByValues.length === 0) return lastSyncData;
    
    return lastSyncData.filter(item => group.startedByValues.includes(item.StartedBy));
  }, [lastSyncData, selectedConnector]);

  const handleTabSelect = (_, data) => {
    setSelectedConnector(data.value);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', data.value);
    setSearchParams(newSearchParams, { replace: true });
  };

  const tabStatuses = useMemo(() => {
    const statuses = {};
    CONNECTOR_GROUPS.forEach((group) => {
      const filtered = group.startedByValues.length === 0
        ? lastSyncData
        : lastSyncData.filter(item => group.startedByValues.includes(item.StartedBy));
      
      const aggregatedSyncData = aggregateSyncData(filtered);
      statuses[group.key] = getMatchedSyncStatus(aggregatedSyncData);
    });
    return statuses;
  }, [lastSyncData]);

  return (
    <div className="sync-details-container">
      <h1>Sync Details</h1>
      <Link 
        to="/customers" 
        style={{ 
          display: 'inline-block', 
          marginBottom: '20px', 
          padding: '8px 16px', 
          backgroundColor: '#0078d4', 
          color: 'white', 
          textDecoration: 'none', 
          borderRadius: '4px',
          fontSize: '14px'
        }}
      >
        Back to Customers
      </Link>

      <CurrentCustomerInformationDetails customer={customer} />

      <style>{tabListStyles}</style>
      <div style={{ marginTop: '24px', marginBottom: '32px' }}>
        <TabList
          className="custom-tab-list"
          selectedValue={selectedConnector}
          onTabSelect={handleTabSelect}
        >
          {CONNECTOR_GROUPS.map((group) => {
            const status = tabStatuses[group.key];
            const percentage = status?.percentage;
            const percentageColor = getPercentageColor(percentage);
            
            return (
              <Tab key={group.key} value={group.key} className="custom-tab">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  {status?.statusIcon}
                  {group.label}
                  {percentage !== null && (
                    <span style={{ color: percentageColor, fontWeight: '500', fontSize: '14px' }}>
                      {percentage}%
                    </span>
                  )}
                </span>
              </Tab>
            );
          })}
        </TabList>
      </div>
      
      {(selectedConnector === 'indvidual' || selectedConnector === 'all') && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          backgroundColor: '#e8f4f8',
          border: '1px solid #0078d4',
          borderRadius: '4px',
          color: '#323130',
          fontSize: '14px'
        }}>
          <strong>Note:</strong> For Individual Syncs, we only consider runs from the last 24 hours.
        </div>
      )}
      
      {loading && <p>Loading...</p>}
      {error && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          backgroundColor: '#fef6f6',
          border: '1px solid #d13438',
          borderRadius: '4px',
          color: '#323130',
          fontSize: '14px'
        }}>
          <strong>{error}</strong>
        </div>
      )}
      {!loading && !error && filteredLastSyncData.length > 0 && (
        <LastSyncDetailsTable syncDataList={filteredLastSyncData} tenantInt={tenantInt} customerId={customerId} />
      )}
      {!loading && !error && filteredLastSyncData.length === 0 && (
        <p>No sync data found for this connector.</p>
      )}

      {!loading && !error && (
        <SyncHistoryDetails tenantInt={tenantInt} customerId={customerId} selectedConnector={selectedConnector} />
      )}
    </div>
  );
}

export default SyncDetails;
